// The History tab's timeline (DES1 `app-parcels-history`). Headless: DATA/PUB/F2 supply events,
// this filters, groups by day and describes each one. It decides no policy and shows no record
// the caller did not authorise; a record is opened by id, read-only.
import type { ObservedImage } from '../media/media-model';

export type HistoryOrigin =
  | { kind: 'builder' }
  | { kind: 'agent'; label: string }
  | { kind: 'terminal'; label: string };

type Base = {
  id: string;
  /** ISO instant. */
  at: string;
  /** Who did it; absent for system work (a refresh). */
  actor?: { name: string };
  origin?: HistoryOrigin;
};

export type HistoryEvent = Base &
  (
    | {
        kind: 'entry';
        summary: string;
        detail?: string;
        /** The image as observed when this entry was made (ART's image VM data). */
        image?: ObservedImage;
      }
    | {
        kind: 'approval';
        outcome: 'approved' | 'declined';
        what: string;
        proposedBy: string;
        decidedBy: string;
        /** "5 of 5 written": absent until the writes settled. */
        written?: { done: number; total: number };
        recordId: string;
      }
    | { kind: 'change'; title: string; detail?: string; draftVersion: number; for?: string }
    | { kind: 'publish'; version: number; audience: string; people: number; requestedBy?: string }
    | { kind: 'unpublish'; version: number }
    | { kind: 'restore'; what: string; target: string }
    | { kind: 'schema'; title: string; detail?: string }
    | { kind: 'refresh'; ok: boolean; detail?: string }
    | { kind: 'source_view'; path: string }
    /** A run with no person skipped a viewer-mode part (C4 `viewer_context_required`). */
    | { kind: 'skipped_run'; run: string; part: string; system: string }
    /** A recoverable operation that has not finished (C4 blocked reason). */
    | { kind: 'operation_blocked'; title: string; reason: 'approval' | 'budget' | 'provider' }
  );

export type HistoryFilter = 'all' | 'entries' | 'approvals' | 'changes' | 'publishes';

export const HISTORY_FILTERS: readonly HistoryFilter[] = [
  'all',
  'entries',
  'approvals',
  'changes',
  'publishes',
];

const GROUP: Record<HistoryEvent['kind'], Exclude<HistoryFilter, 'all'>> = {
  entry: 'entries',
  approval: 'approvals',
  change: 'changes',
  schema: 'changes',
  restore: 'changes',
  source_view: 'changes',
  refresh: 'changes',
  skipped_run: 'changes',
  operation_blocked: 'changes',
  publish: 'publishes',
  unpublish: 'publishes',
};

export function filterHistory(events: readonly HistoryEvent[], filter: HistoryFilter) {
  return filter === 'all' ? [...events] : events.filter((event) => GROUP[event.kind] === filter);
}

export type HistoryDay = { key: string; offset: number; at: string; events: HistoryEvent[] };

const dayKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** Newest first, grouped by the viewer's local day; `offset` 0 is today, 1 yesterday. */
export function groupByDay(events: readonly HistoryEvent[], now: Date): HistoryDay[] {
  const sorted = [...events].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const days: HistoryDay[] = [];
  for (const event of sorted) {
    const date = new Date(event.at);
    const key = dayKey(date);
    let day = days.find((d) => d.key === key);
    if (!day) {
      const startOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
      day = {
        key,
        offset: Math.round((startOfToday - startOfDay) / 86_400_000),
        at: event.at,
        events: [],
      };
      days.push(day);
    }
    day.events.push(event);
  }
  return days;
}

/** Initials for the avatar: the first letters of the first and last name. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/u).filter(Boolean);
  if (parts.length === 0) return '';
  const first = Array.from(parts[0])[0] ?? '';
  const last = parts.length > 1 ? (Array.from(parts[parts.length - 1])[0] ?? '') : '';
  return (first + last).toUpperCase();
}
